<template>
  <div class="compare" ref="root">
    <div class="frame" :style="frameStyle">
      <div v-if="isLoading" class="loading-overlay">
        <v-progress-circular indeterminate color="primary" size="32" />
      </div>
      <img
        :src="baseSrc"
        :srcset="beforeSrcSet"
        :sizes="sizes"
        :alt="t('compare.before')"
        :class="imageClass"
        :loading="loadingAttr"
        :fetchpriority="priority ? 'high' : undefined"
        decoding="async"
        @load="handleBeforeLoaded"
      />
      <div class="label label-left">{{ t('compare.before') }}</div>
      <div class="overlay" :style="overlayStyle">
        <img
          :src="overlaySrc"
          :srcset="afterSrcSet"
          :sizes="sizes"
          :alt="t('compare.after')"
          :class="imageClass"
          :loading="loadingAttr"
          :fetchpriority="priority ? 'low' : undefined"
          decoding="async"
          @load="handleAfterLoaded"
        />
      </div>
      <div class="label label-right">{{ t('compare.after') }}</div>
    </div>
    <div
      class="handle"
      :style="{ left: `${position}%` }"
      @pointerdown="startDrag"
    >
      <div class="bar"></div>
      <div class="knob">
        <v-icon size="16">mdi-unfold-more-horizontal</v-icon>
      </div>
    </div>
    <input
      type="range"
      class="range"
      min="0"
      max="100"
      :value="position"
      @input="onInput"
      :aria-label="t('compare.sliderLabel')"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { lockBodyScroll, unlockBodyScroll } from '../../lib/useBodyScrollLock';

const props = defineProps<{
  beforeSrc: string;
  afterSrc: string;
  beforeSrcSet?: string;
  afterSrcSet?: string;
  sizes?: string;
  initial?: number;
  fit?: "cover" | "contain";
  height?: string;
  reverse?: boolean;
  priority?: boolean;
}>();

const { t } = useI18n();

const position = ref(50);
const root = ref<HTMLElement | null>(null);
const beforeLoaded = ref(false);
const afterLoaded = ref(false);
const isLoading = computed(() => !beforeLoaded.value || !afterLoaded.value);
let dragging = false;

const imageClass = computed(() => [
  "image",
  { "image-contain": props.fit === "contain" },
]);

const baseSrc = computed(() =>
  props.reverse ? props.afterSrc : props.beforeSrc,
);
const overlaySrc = computed(() =>
  props.reverse ? props.beforeSrc : props.afterSrc,
);
const beforeSrcSet = computed(() =>
  props.reverse ? props.afterSrcSet : props.beforeSrcSet,
);
const afterSrcSet = computed(() =>
  props.reverse ? props.beforeSrcSet : props.afterSrcSet,
);
const sizes = computed(() => props.sizes);
const priority = computed(() => props.priority ?? false);
const loadingAttr = computed(() => (priority.value ? "eager" : "lazy"));

const frameStyle = computed(() => {
  if (!props.height) return undefined;
  return {
    height: props.height,
    aspectRatio: "auto",
  };
});

const overlayStyle = computed(() => ({
  clipPath: `inset(0 0 0 ${position.value}%)`,
}));

function clamp(value: number) {
  return Math.min(100, Math.max(0, value));
}

function setFromEvent(event: PointerEvent) {
  if (!root.value) return;
  const rect = root.value.getBoundingClientRect();
  const percent = ((event.clientX - rect.left) / rect.width) * 100;
  position.value = clamp(percent);
}

function startDrag(event: PointerEvent) {
  dragging = true;
  setFromEvent(event);
  lockBodyScroll();
  window.addEventListener('pointermove', onDrag);
  window.addEventListener('pointerup', stopDrag);
}

function onDrag(event: PointerEvent) {
  if (!dragging) return;
  setFromEvent(event);
}

function stopDrag() {
  dragging = false;
  unlockBodyScroll();
  window.removeEventListener('pointermove', onDrag);
  window.removeEventListener('pointerup', stopDrag);
}

function onInput(event: Event) {
  const target = event.target as HTMLInputElement | null;
  if (!target) return;
  position.value = clamp(Number(target.value));
}

function handleBeforeLoaded() {
  beforeLoaded.value = true;
}

function handleAfterLoaded() {
  afterLoaded.value = true;
}

watch(
  () => [props.beforeSrc, props.afterSrc],
  () => {
    beforeLoaded.value = false;
    afterLoaded.value = false;
  },
);

onBeforeUnmount(() => stopDrag());
</script>

<style scoped>
.compare {
  position: relative;
  width: 100%;
  border-radius: 18px;
  overflow: hidden;
  border: 1px solid var(--accent-border-weak);
  background: var(--surface-overlay);
  box-shadow: var(--panel-shadow);
}

.frame {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  overflow: hidden;
}

.loading-overlay {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  z-index: 1;
  background: var(--surface-overlay);
  backdrop-filter: blur(2px);
}

.image {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.image-contain {
  object-fit: contain;
  background: var(--surface-overlay);
}

.overlay {
  position: absolute;
  inset: 0;
  overflow: hidden;
}

.label {
  position: absolute;
  top: 14px;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 12px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  background: var(--surface-overlay-strong);
  border: 1px solid var(--accent-border);
  color: var(--app-muted-90);
  backdrop-filter: blur(8px);
  z-index: 2;
}

.label-left {
  left: 14px;
}

.label-right {
  right: 14px;
}

.handle {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ew-resize;
  pointer-events: auto;
  touch-action: none;
}

.bar {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 2px;
  background: var(--app-muted-70);
}

.knob {
  position: relative;
  z-index: 2;
  width: 44px;
  height: 44px;
  border-radius: 999px;
  display: grid;
  place-items: center;
  background: var(--surface-soft);
  border: 1px solid var(--accent-border);
  box-shadow: var(--accent-shadow-soft);
  color: var(--app-muted-80);
}

.range {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: ew-resize;
  touch-action: none;
}

@media (max-width: 600px) {
  .frame {
    aspect-ratio: 4 / 3;
  }
}
</style>
